// for now we use router elements from https://github.com/supasate/connected-react-router#usage
// and in the same way
import {
  ConnectedRouter,
  connectRouter,
  routerMiddleware,
} from 'connected-react-router'

import { navigationEvent } from './store/operations'
import {
  PUSH,
  GO,
  REPLACE,
  NEW_TAB,
  NEW_TAB_SAME_SITE,
  GOFORWARD,
  GOBACK,
  DOWNLOAD_LINK,
  OPEN,
  NAVIGATION_EVENT,
} from './store/types'

export default class NavigationHelper {
  static NAMESPACE = 'router'
  static NAVIGATION_EVENT = NAVIGATION_EVENT

  static types = {
    DOWNLOAD_LINK,
    GO,
    GOBACK,
    GOFORWARD,
    NEW_TAB,
    NEW_TAB_SAME_SITE,
    OPEN,
    PUSH,
    REPLACE,
  }

  static isObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]'
  }

  static isADuplicatePath(originalPathname, newPathname) {
    const isDuplicatePath = originalPathname.endsWith(newPathname)

    return isDuplicatePath
  }

  // enables both usage of passing and involing with mapper and spread and without
  //  connect(NavigationHelper.mapStateToProps(mapper, spread))(Component) -- with mapper
  //  connect(NavigationHelper.mapStateToProps)(Component) -- without mapper
  static mapStateToProps = (mapperOrState, spread) => {
    let mapper, state
    const _mapStateToProps = (state, ownProps) => {
      const router = NavigationHelper.getRouter(state)
      let mappedRouter = mapper ? mapper(router, state, ownProps) : router
      mappedRouter = spread ? { ...mappedRouter } : { router: mappedRouter }
      return mappedRouter
    }
    if (NavigationHelper.isObject(mapperOrState)) {
      state = mapperOrState
      return _mapStateToProps(state)
    }
    mapper = mapperOrState
    return _mapStateToProps
  }

  static mapDispatchToProps = {
    navigationEvent,
  }
  static ConnectedRouter() {
    return ConnectedRouter
  }
  static connectRouter() {
    return connectRouter
  }
  static navigationMiddleware() {
    return routerMiddleware
  }
  static getRouter(state) {
    return state[NavigationHelper.NAMESPACE]
  }
  static getLocation(state) {
    const router = NavigationHelper.getRouter(state)
    return router?.location || {}
  }
  static getPathname(state) {
    const location = NavigationHelper.getLocation(state)
    return location?.pathname || ''
  }
  static getSearch(state) {
    const location = NavigationHelper.getLocation(state)
    return location?.search || ''
  }

  static getQueryParamsByKey(state, keys = []) {
    const search = NavigationHelper.getSearch(state)
    const queryParams = new URLSearchParams(search)
    if (!Array.isArray(keys)) {
      keys = [keys]
    }
    const paramsByKey = {}
    for (const key of keys) {
      const value = queryParams.get(key)
      paramsByKey[key] = value
    }
    return paramsByKey
  }

  static getHash(state) {
    const location = NavigationHelper.getLocation(state)
    return location?.hash || ''
  }

  static generateRouteWithQueryParams(route, paramsByKey) {
    const queryString = Object.keys(paramsByKey)
      .map(key => key + '=' + paramsByKey[key])
      .join('&')
    return queryString ? `${route}?${queryString}` : route
  }

  static generateUrlWithDepthForRoute(pathname, route, depth) {
    const startSlice = 1
    const endSlice = depth + 1
    const startPath = pathname.split('/').slice(startSlice, endSlice)
    const fullRoute = startPath.reduceRight((result, path) => {
      return `/${path}${result}`
    }, `/${route}`)

    return fullRoute
  }

  /**
   * Builds a navigation URL with query parameters.
   *
   * @param {Object} params - Parameters object
   * @param {string} params.pathname - Current route pathname
   * @param {Object} params.params - Query parameters in URL format
   *   Example: { site: '123', sortKey: 'schools', orderBy: 'asc', class: 'instance-123' }
   *   Only truthy values will be included (null/undefined/empty string are excluded)
   * @returns {string} Generated URL with query parameters
   */
  static buildNavigationUrl({ pathname, params = {} }) {
    const queryParamsByKey = {}

    Object.keys(params).forEach(key => {
      const value = params[key]
      // Only include if value is truthy (null/undefined/empty string excludes it)
      if (value !== undefined && value !== null && value !== '') {
        queryParamsByKey[key] = value.toString()
      }
    })

    return NavigationHelper.generateRouteWithQueryParams(
      pathname,
      queryParamsByKey,
    )
  }

  /**
   * Creates a navigation handler function that filters params to managedParamKeys and dispatches.
   * Container is responsible for merging current values with updates before calling the handler.
   *
   * @param {Object} config - Configuration object
   * @param {string[]} config.managedParamKeys - Array of URL param names to manage
   *   Example: ['site', 'sortKey', 'orderBy', 'currentPage', 'class']
   * @param {Object} config.location - Router location object (for pathname)
   * @param {Function} config.navigationEvent - Redux action creator for navigation
   * @returns {Function} Navigation handler function
   *
   * @example
   * const navigationHandler = NavigationHelper.createNavigationHandler({
   *   managedParamKeys: ['site', 'sortKey', 'class'],
   *   location,
   *   navigationEvent,
   * })
   *
   * // Container passes complete set of params it wants (after merging current + updates)
   * navigationHandler({
   *   site: '123',
   *   sortKey: 'lessonsDone',
   *   class: 'instance-123',
   * }, NavigationHelper.types.PUSH)
   */
  static createNavigationHandler({
    managedParamKeys = [],
    location,
    navigationEvent,
  }) {
    return (params = {}, type = NavigationHelper.types.REPLACE) => {
      const pathname = location?.pathname || ''

      // Filter to only include managed params
      const filteredParams = {}
      managedParamKeys.forEach(key => {
        const value = params[key]
        // Only include if value is truthy (null/undefined/empty string excluded)
        if (value !== undefined && value !== null && value !== '') {
          filteredParams[key] = value
        }
      })

      const url = NavigationHelper.buildNavigationUrl({
        pathname,
        params: filteredParams,
      })
      navigationEvent(url, type)
    }
  }
}
